import { formatIngredientLabel, normalize } from './recipes';

export const PANTRY_STORAGE_KEY = 'recipe-owned-ingredients-v1';
export const PANTRY_FRESHNESS_REVIEW_DAYS = 7;

const normalizeFreshnessText = (value) => normalize(value)
  .toLocaleLowerCase('da-DK')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '');

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(typeof value === 'string' ? value.replace(',', '.') : value);
  return Number.isFinite(number) ? number : null;
};

const getQuantityFromAmountLabel = (amountLabel) => {
  const match = normalize(amountLabel).match(/(\d+(?:[.,]\d+)?)\s*([^\s]+)/);
  if (!match) {
    return { amount: null, unit: '' };
  }

  return {
    amount: toOptionalNumber(match[1]),
    unit: normalize(match[2]),
  };
};

const getPantryProductName = (productName) => formatIngredientLabel(productName)
  .replace(/\s+\d+(?:[.,]\d+)?\s*(?:mg|g|kg|ml|cl|dl|l|stk)\s*$/i, '')
  .trim();

const getEntryTimestamp = (entry) => {
  const timestamp = new Date(entry?.updatedAt || '').getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const coalesceDuplicatePantryEntries = (state) => {
  const result = {};
  const keyByIdentity = new Map();

  Object.entries(state).forEach(([key, entry]) => {
    const identity = [
      getPantryKey(entry.productName),
      normalize(entry.quantityUnit).toLocaleLowerCase('da-DK'),
    ].join('|');
    const existingKey = keyByIdentity.get(identity);
    if (!existingKey) {
      result[key] = entry;
      keyByIdentity.set(identity, key);
      return;
    }

    const existingEntry = result[existingKey];
    if (getEntryTimestamp(entry) >= getEntryTimestamp(existingEntry)) {
      delete result[existingKey];
      result[key] = entry;
      keyByIdentity.set(identity, key);
    }
  });

  return result;
};

export function getPantryFreshnessKind({ productName = '', category = '', source = '' } = {}) {
  if (normalize(source).toLowerCase() === 'leftover') {
    return 'short';
  }

  const text = normalizeFreshnessText(`${productName} ${category}`);
  if (!text) {
    return 'unknown';
  }

  const isClearlyDurable = /(frossen|frost|konserves|dåse|daase|dase|tørret|toerret|pasta|eddike|krydder|bouillon|kaffe|honning|syltet)/.test(text)
    || /\b(ris|mel|sukker|olie|te)\b/.test(text)
    || /(^|\s)(æg|aeg)(\s|$)/.test(text);

  if (isClearlyDurable) {
    return 'long';
  }

  if (/(laks|fisk|torsk|reje|skaldyr|kylling|kalkun|hakket kød|hakket koed|svinekød|svinekoed|oksekød|oksekoed|kalv|flæsk|flaesk|mælk|maelk|fløde|floede|yoghurt|skyr|creme fraiche|hytteost|mozzarella|salat|spinat|rucola|friske urter|persille|koriander|basilikum|champignon|svampe|bær|baer|hindbær|hindbaer|jordbær|jordbaer)/.test(text)) {
    return 'short';
  }

  return 'unknown';
}

export function getPantryKey(productName) {
  return normalize(productName).toLowerCase();
}

export function createPantryEntry({
  productName,
  amountLabel = '',
  source = 'manual',
  note = '',
  category = '',
  freshnessKind = '',
  quantityAmount = null,
  quantityUnit = '',
  packageCount = null,
  packageSize = null,
  packageUnit = '',
  sourceRecipeId = '',
  trackingMode = 'precise',
  confidence = '',
}) {
  const timestamp = new Date().toISOString();
  const legacyQuantity = getQuantityFromAmountLabel(amountLabel);
  const normalizedQuantityAmount = toOptionalNumber(quantityAmount) ?? legacyQuantity.amount;
  const normalizedPackageCount = toOptionalNumber(packageCount);
  const normalizedPackageSize = toOptionalNumber(packageSize);

  return {
    owned: true,
    productName: getPantryProductName(productName),
    source,
    amountLabel: normalize(amountLabel),
    note: normalize(note),
    category: normalize(category),
    freshnessKind: normalize(freshnessKind) || getPantryFreshnessKind({ productName, category, source }),
    quantityAmount: normalizedQuantityAmount,
    quantityUnit: normalize(quantityUnit) || legacyQuantity.unit,
    packageCount: normalizedPackageCount,
    packageSize: normalizedPackageSize,
    packageUnit: normalize(packageUnit),
    sourceRecipeId: normalize(sourceRecipeId),
    trackingMode: trackingMode === 'smart' ? 'smart' : 'precise',
    confidence: ['confirmed', 'estimated'].includes(confidence) ? confidence : '',
    confirmedAt: timestamp,
    updatedAt: timestamp,
  };
}

export function normalizePantryState(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const normalizedState = Object.entries(value).reduce((state, [rawKey, rawEntry]) => {
    const key = getPantryKey(rawKey);
    if (!key || !rawEntry || typeof rawEntry !== 'object' || rawEntry.owned === false) {
      return state;
    }

    const productName = getPantryProductName(rawEntry.productName || rawKey);
    const source = normalize(rawEntry.source) || 'leftover';
    const category = normalize(rawEntry.category);
    const updatedAt = normalize(rawEntry.updatedAt) || new Date().toISOString();
    const legacyQuantity = getQuantityFromAmountLabel(rawEntry.amountLabel);
    const packageCount = toOptionalNumber(rawEntry.packageCount);
    const packageSize = toOptionalNumber(rawEntry.packageSize);
    const hasStructuredPackage = packageCount !== null && packageSize !== null;
    const storedQuantityAmount = toOptionalNumber(rawEntry.quantityAmount);
    const quantityAmount = storedQuantityAmount ?? (hasStructuredPackage ? null : legacyQuantity.amount);
    const quantityUnit = normalize(rawEntry.quantityUnit)
      || (hasStructuredPackage && storedQuantityAmount === null ? '' : legacyQuantity.unit);
    state[key] = {
      owned: true,
      productName,
      source,
      amountLabel: normalize(rawEntry.amountLabel),
      note: normalize(rawEntry.note),
      category,
      freshnessKind: normalize(rawEntry.freshnessKind)
        || getPantryFreshnessKind({ productName, category, source }),
      quantityAmount,
      quantityUnit,
      packageCount,
      packageSize,
      packageUnit: normalize(rawEntry.packageUnit),
      sourceRecipeId: normalize(rawEntry.sourceRecipeId),
      trackingMode: rawEntry.trackingMode === 'smart' ? 'smart' : 'precise',
      confidence: ['confirmed', 'estimated'].includes(rawEntry.confidence) ? rawEntry.confidence : '',
      confirmedAt: normalize(rawEntry.confirmedAt) || updatedAt,
      updatedAt,
    };
    return state;
  }, {});

  return coalesceDuplicatePantryEntries(normalizedState);
}

export function getPantryFreshnessStatus(entry, now = Date.now()) {
  const freshnessKind = normalize(entry?.freshnessKind)
    || getPantryFreshnessKind(entry);
  const confirmedAt = new Date(entry?.confirmedAt || entry?.updatedAt || '');
  const nowValue = now instanceof Date ? now.getTime() : Number(now);

  if (freshnessKind !== 'short' || Number.isNaN(confirmedAt.getTime()) || !Number.isFinite(nowValue)) {
    return {
      freshnessKind,
      ageDays: 0,
      requiresConfirmation: false,
    };
  }

  const ageDays = Math.max(0, Math.floor((nowValue - confirmedAt.getTime()) / 86400000));

  return {
    freshnessKind,
    ageDays,
    requiresConfirmation: ageDays >= PANTRY_FRESHNESS_REVIEW_DAYS,
  };
}

export function consumePantryEntry(entry, allocation, timestamp = new Date().toISOString()) {
  const currentAmount = toOptionalNumber(entry?.quantityAmount);
  const allocatedAmount = toOptionalNumber(allocation?.amount);
  const currentUnit = normalize(entry?.quantityUnit);
  const allocatedUnit = normalize(allocation?.unit);

  if (
    currentAmount === null
    || allocatedAmount === null
    || !currentUnit
    || currentUnit.toLocaleLowerCase('da-DK') !== allocatedUnit.toLocaleLowerCase('da-DK')
  ) {
    return null;
  }

  const remainingAmount = Math.max(0, currentAmount - allocatedAmount);
  if (remainingAmount <= 0) {
    return null;
  }

  const formattedAmount = Number.isInteger(remainingAmount)
    ? remainingAmount.toString()
    : remainingAmount.toLocaleString('da-DK', { maximumFractionDigits: 2 });

  return {
    ...entry,
    amountLabel: `${formattedAmount} ${currentUnit} tilbage`,
    quantityAmount: remainingAmount,
    packageCount: null,
    confirmedAt: timestamp,
    updatedAt: timestamp,
  };
}

export function loadPantryState() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const savedValue = window.localStorage.getItem(PANTRY_STORAGE_KEY);
    return normalizePantryState(savedValue ? JSON.parse(savedValue) : {});
  } catch {
    return {};
  }
}

export function savePantryState(state) {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    window.localStorage.setItem(PANTRY_STORAGE_KEY, JSON.stringify(normalizePantryState(state)));
    return true;
  } catch {
    return false;
  }
}

export function pantryStateToItems(state) {
  return Object.entries(normalizePantryState(state))
    .map(([key, entry]) => ({
      key,
      ...entry,
    }))
    .sort((left, right) => normalize(right.updatedAt).localeCompare(normalize(left.updatedAt)));
}

export function formatPantryUpdatedAt(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('da-DK', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
